import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { IdCard, Plus, Pencil, Trash2, Loader2, Users, Search } from 'lucide-react';
import { toast } from 'sonner';

export const DesignationsAdmin = () => {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', level: '', department: '_none', is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/designations?include_inactive=true'),
        api.get('/departments'),
      ]);
      if (r1.data?.success) setRows(r1.data.data || []);
      if (r2.data?.success) setDepartments(r2.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load designations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', level: '', department: '_none', is_active: true });
    setOpenDialog(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '',
      description: d.description || '',
      level: d.level || '',
      department: d.department || '_none',
      is_active: d.is_active !== false,
    });
    setOpenDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        department: form.department === '_none' ? '' : form.department,
      };
      if (editing) {
        await api.put(`/designations/${editing.id}`, payload);
        toast.success('Designation updated');
      } else {
        await api.post('/designations', payload);
        toast.success('Designation created');
      }
      setOpenDialog(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save designation');
    } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/designations/${d.id}`, { is_active: !d.is_active });
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/designations/${deleteTarget.id}`);
      toast.success('Designation deleted');
      setDeleteTarget(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const filtered = rows.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.name || '').toLowerCase().includes(q)
      || (r.department || '').toLowerCase().includes(q)
      || (r.level || '').toLowerCase().includes(q);
  });

  return (
    <Card data-testid="designations-admin-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <IdCard className="h-5 w-5 text-indigo-500" />
            <CardTitle className="text-lg">HR · Designations</CardTitle>
          </div>
          <Button size="sm" onClick={openCreate} data-testid="add-designation-btn">
            <Plus className="h-4 w-4 mr-1.5" />Add Designation
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Standardise employee titles for appraisals and payroll bands. The Employees form renders this as a combobox — pick from the list or type a custom title. Existing free-text designations were auto-imported on first boot. Renaming cascades to every employee record.
        </p>
        <div className="relative mt-2">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search by name, department, level…"
            className="pl-8 h-8 text-xs"
            data-testid="designations-filter"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">
            {filter ? 'No matches.' : 'No designations yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="designations-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Title</th>
                  <th className="px-4 py-2 text-left font-semibold">Department</th>
                  <th className="px-4 py-2 text-left font-semibold">Level</th>
                  <th className="px-4 py-2 text-right font-semibold">Employees</th>
                  <th className="px-4 py-2 text-center font-semibold">Active</th>
                  <th className="px-4 py-2 text-right font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((d) => (
                  <tr key={d.id} data-testid={`designation-row-${d.id}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5 font-medium">{d.name}</td>
                    <td className="px-4 py-2.5">
                      {d.department
                        ? <Badge variant="outline" className="text-[10px]">{d.department}</Badge>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {d.level
                        ? <Badge className="text-[10px] bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30">{d.level}</Badge>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {d.employees_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Users className="h-3 w-3 text-muted-foreground" />{d.employees_count}
                        </span>
                      ) : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Switch
                        checked={d.is_active !== false}
                        onCheckedChange={() => toggleActive(d)}
                        data-testid={`des-active-toggle-${d.id}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(d)} data-testid={`edit-des-${d.id}`} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => setDeleteTarget(d)}
                          data-testid={`delete-des-${d.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="designation-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Designation' : 'New Designation'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Renaming this title will update every employee record assigned to it.'
                : 'Add a title to the dropdown. Existing free-text titles in employees were already imported.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Software Engineer"
                data-testid="designation-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Department <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Select
                  value={form.department}
                  onValueChange={v => setForm({ ...form, department: v })}
                >
                  <SelectTrigger data-testid="designation-department-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Level <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  value={form.level}
                  onChange={e => setForm({ ...form, level: e.target.value })}
                  placeholder="e.g. L1, L2, L3"
                  data-testid="designation-level-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What this role does"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Inactive titles are hidden from the employee combobox.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="designation-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-designation-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Save Changes' : 'Create Designation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="designation-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.employees_count > 0
                ? <span className="text-red-500">This title is held by {deleteTarget.employees_count} employee(s). Reassign them first — deletion will be blocked by the server.</span>
                : 'This will soft-delete the designation. The slug becomes available for reuse later.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-des-btn"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DesignationsAdmin;
